import { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';

const Page = styled.div`
  min-height: 100vh;
  background: #0A0A0A;
  padding-top: 76px;
`;

const Hero = styled.div`
  background: linear-gradient(180deg, #0D0D0D 0%, #0A0A0A 100%);
  border-bottom: 1px solid #1A1A1A;
  padding: 80px 2rem 64px;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -80px;
    left: 50%;
    transform: translateX(-50%);
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(244,121,32,0.07) 0%, transparent 65%);
    pointer-events: none;
  }
`;

const EyebrowWrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
`;

const Line = styled.div`
  width: 28px;
  height: 2px;
  background: #F47920;
  border-radius: 1px;
`;

const Eyebrow = styled.span`
  font-size: 0.72rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #F47920;
  font-weight: 600;
`;

const HeroTitle = styled.h1`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: clamp(2.8rem, 6vw, 5rem);
  font-weight: 900;
  text-transform: uppercase;
  line-height: 0.95;
  letter-spacing: -0.02em;
  margin: 0 0 1.25rem;
`;

const Accent = styled.span`
  color: #F47920;
`;

const HeroSub = styled.p`
  font-size: 1rem;
  color: #777;
  max-width: 520px;
  margin: 0 auto;
  line-height: 1.7;
`;

const FormSection = styled.section`
  max-width: 720px;
  margin: 0 auto;
  padding: 64px 2rem 100px;

  @media (max-width: 768px) { padding: 48px 1.25rem 80px; }
`;

const FormCard = styled.div`
  background: #111;
  border: 1px solid #1E1E1E;
  border-radius: 20px;
  padding: 2.5rem;

  @media (max-width: 480px) { padding: 1.75rem 1.25rem; }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.1rem;
`;

const Label = styled.label`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #888;
`;

const inputBase = `
  background: #0D0D0D;
  border: 1px solid #252525;
  border-radius: 10px;
  color: #fff;
  font-size: 0.95rem;
  font-family: inherit;
  outline: none;
  transition: border-color 0.2s ease;
  width: 100%;
  box-sizing: border-box;

  &::placeholder { color: #3A3A3A; }
  &:focus { border-color: #F47920; }
`;

const Input = styled.input`
  ${inputBase}
  padding: 0.8rem 1rem;
`;

const Textarea = styled.textarea`
  ${inputBase}
  padding: 0.8rem 1rem;
  resize: vertical;
  min-height: 110px;
  line-height: 1.6;
`;

const FileZone = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 1.5rem 1rem;
  background: #0D0D0D;
  border: 1.5px dashed #2E2E2E;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
  text-align: center;

  &:hover {
    border-color: #F47920;
    background: rgba(244,121,32,0.04);
  }

  input[type="file"] { display: none; }

  svg {
    width: 28px;
    height: 28px;
    color: #444;
    transition: color 0.2s ease;
  }

  &:hover svg { color: #F47920; }
`;

const FileHint = styled.span`
  font-size: 0.78rem;
  color: #555;
  letter-spacing: 0.03em;
`;

const FileName = styled.span`
  font-size: 0.82rem;
  color: #F47920;
  font-weight: 600;
  word-break: break-all;
`;

const SubmitBtn = styled(motion.button)`
  width: 100%;
  padding: 1rem;
  background: #F47920;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  cursor: pointer;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: background 0.2s ease;

  &:hover:not(:disabled) { background: #FF8C3A; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }

  svg { width: 18px; height: 18px; }
`;


const BackLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #555;
  margin-bottom: 2rem;
  transition: color 0.2s ease;
  cursor: pointer;

  &:hover { color: #F47920; }
  svg { width: 14px; height: 14px; }
`;

const SuccessCard = styled(motion.div)`
  background: #111;
  border: 1px solid rgba(37,211,102,0.2);
  border-radius: 20px;
  padding: 3rem 2rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

const SuccessIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(37,211,102,0.1);
  display: flex;
  align-items: center;
  justify-content: center;

  svg { width: 28px; height: 28px; color: #25D366; }
`;

const SuccessTitle = styled.h2`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 1.8rem;
  font-weight: 800;
  text-transform: uppercase;
  margin: 0;
`;

const SuccessText = styled.p`
  color: #777;
  font-size: 0.95rem;
  line-height: 1.7;
  max-width: 380px;
  margin: 0;
`;

export default function Empleo() {
  const [searchParams] = useSearchParams();
  const enviado = searchParams.get('ok') === '1';
  const [cvFile, setCvFile] = useState(null);
  const [cvError, setCvError] = useState(null);

  const handleFile = (e) => {
    const file = e.target.files[0] || null;
    if (file && file.size > 10 * 1024 * 1024) {
      setCvError('El archivo supera los 10 MB. Por favor sube un archivo más pequeño.');
      setCvFile(null);
      e.target.value = '';
      return;
    }
    setCvError(null);
    setCvFile(file);
  };

  const nextUrl = `${window.location.origin}/empleo?ok=1`;

  return (
    <Page>
      <Hero>
        <EyebrowWrap>
          <Line />
          <Eyebrow>Únete al equipo</Eyebrow>
          <Line />
        </EyebrowWrap>
        <HeroTitle>
          Empleos<br /><Accent>Fereima</Accent>
        </HeroTitle>
        <HeroSub>
          Somos una empresa en crecimiento. Si tienes experiencia en la industria
          cárnica o quieres aprender, nos encantaría conocerte.
        </HeroSub>
      </Hero>

      <FormSection>
        <BackLink href="/">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          Volver al inicio
        </BackLink>

        {enviado ? (
          <SuccessCard
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <SuccessIcon>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </SuccessIcon>
            <SuccessTitle>¡Solicitud enviada!</SuccessTitle>
            <SuccessText>
              Recibimos tu información y CV. Nos pondremos en contacto contigo a la brevedad.
            </SuccessText>
            <BackLink href="/empleo" style={{ marginBottom: 0, marginTop: '0.5rem' }}>
              Enviar otra solicitud
            </BackLink>
          </SuccessCard>
        ) : (
          <FormCard>
            <form
              action="https://formsubmit.co/administracion@carnesfereima.com"
              method="POST"
              encType="multipart/form-data"
            >
              <input type="hidden" name="_subject" value="Nueva solicitud de empleo — Empleos Fereima" />
              <input type="hidden" name="_captcha" value="false" />
              <input type="hidden" name="_next" value={nextUrl} />

              <Row>
                <Field>
                  <Label htmlFor="from_name">Nombre y Apellidos *</Label>
                  <Input id="from_name" name="from_name" type="text" placeholder="Tu nombre completo" required />
                </Field>
                <Field>
                  <Label htmlFor="phone">Número de Teléfono *</Label>
                  <Input id="phone" name="phone" type="tel" placeholder="395 000 0000" required />
                </Field>
              </Row>

              <Field>
                <Label htmlFor="from_email">Correo Personal *</Label>
                <Input id="from_email" name="from_email" type="email" placeholder="correo@ejemplo.com" required />
              </Field>

              <Field>
                <Label htmlFor="position">Área de Empleo Deseada *</Label>
                <Textarea
                  id="position"
                  name="position"
                  placeholder="Describe el área o puesto al que te gustaría aplicar..."
                  style={{ minHeight: '90px' }}
                  required
                />
              </Field>

              <Field>
                <Label htmlFor="experience">Formación y Experiencia Laboral *</Label>
                <Textarea
                  id="experience"
                  name="experience"
                  placeholder="Cuéntanos tu formación académica y experiencia laboral relevante..."
                  required
                />
              </Field>

              <Field>
                <Label>Currículum Vitae</Label>
                <FileZone>
                  <input
                    type="file"
                    name="attachment"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFile}
                  />
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/>
                    <line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                  {cvFile ? (
                    <FileName>{cvFile.name}</FileName>
                  ) : (
                    <>
                      <FileHint>Haz clic para subir tu CV</FileHint>
                      <FileHint>PDF, DOC o DOCX</FileHint>
                    </>
                  )}
                </FileZone>
                {cvError && (
                  <FileHint style={{ color: '#ff6b6b', marginTop: '0.4rem' }}>{cvError}</FileHint>
                )}
              </Field>

              <SubmitBtn
                disabled={!!cvError}
                type="submit"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="22" y1="2" x2="11" y2="13"/>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                </svg>
                Enviar Solicitud
              </SubmitBtn>
            </form>
          </FormCard>
        )}
      </FormSection>
    </Page>
  );
}
